import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { CardContent, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import {
  PermissionEnum,
  useConfig,
  useNotification,
  useOidcWithAdmin,
  usePermission
} from '@revenuewell/front-end-bundle';
import { usePatientProfile } from 'apps/wrapper/src/hooks/use-patient-profile';
import moment from 'moment-timezone';
import { ContactApi } from '../../../../../services/api-client/contact-api';
import PatientFeedbackService from '../../../../../services/patient-feedback-service/patient-feedback-service';
import { Survey } from '../../../../../services/patient-feedback-service/patient-feedback-service-types';
import { Card } from '../../../card/card.style';
import { CloseDialogButton, Dialog, DialogContent, DialogContentWrapper } from '../../../dialog/dialog.styles';
import { FeatureNotAllowedModal } from '../../../feature/feature-not-allowed/feature-not-allowed';
import { CardTitle, DialogTitle, InfoTitle, Score, Table, YesNo } from '../dialog.styles';
import { SurveyResultsSkeleton } from './survey-results.skeleton';

const scoreClass = (score: number) => {
  if (score > 80) {
    return 'high';
  }

  if (score > 50) {
    return 'warning';
  }

  return 'danger';
};

export interface SurveyResultsDialogProps {
  open: boolean;
  surveyId: number | null;
  onClose: () => void;
}

export const SurveyResultsDialog: FC<SurveyResultsDialogProps> = (props) => {
  const { open, surveyId, onClose } = props;
  const { config } = useConfig();
  const oidc = useOidcWithAdmin();
  const { showError } = useNotification();
  const { contactDetails, locationId } = usePatientProfile();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { hasPermission } = usePermission();

  const patientFeedbackService = useMemo(
    () => PatientFeedbackService.getInstance(config, oidc.oidcService),
    [config, oidc]
  );

  const contactApi = useMemo(() => ContactApi.getInstance(), [ContactApi]);

  const initiate = useCallback(
    async (surveyId: number, locId: number, patientId: number) => {
      try {
        setIsLoading(true);

        const surveyResponse = await patientFeedbackService.getSurveyById(locId, patientId, surveyId);

        setSurvey(surveyResponse);
        setIsLoading(false);
      } catch (error) {
        showError('Failed to load patient data');
        setIsLoading(false);
        onClose();
      }
    },
    [config, contactApi]
  );

  useEffect(() => {
    if (open && surveyId && locationId && contactDetails) {
      if (hasPermission(PermissionEnum.ViewSurvey)) {
        setSurvey(null);
        initiate(surveyId, locationId, contactDetails.id);
      }
    } else {
      onClose();
    }
  }, [open, surveyId, locationId, contactDetails]);

  if (open && !hasPermission(PermissionEnum.ViewReview)) return <FeatureNotAllowedModal onClose={onClose} />;

  return (
    <Dialog open={open} maxWidth={false}>
      <DialogContentWrapper>
        <DialogTitle sx={{ cursor: 'default' }}>
          {contactDetails?.firstName} {contactDetails?.lastName}
          <CloseDialogButton onClick={onClose} />
        </DialogTitle>
        <DialogContent>
          {isLoading && <SurveyResultsSkeleton />}
          {!isLoading && (
            <Card>
              <CardContent style={{ paddingBottom: 0 }}>
                <CardTitle>
                  Survey Results
                  <span>{moment(survey?.date).format('MM/DD/YYYY hh:mm A')}</span>
                </CardTitle>
                <Table className="table-info">
                  <TableBody>
                    <TableRow>
                      <TableCell style={{ width: '22%' }}>
                        <InfoTitle>Score:</InfoTitle>
                      </TableCell>
                      <TableCell>
                        <Score className={scoreClass(survey?.score || 0)}>{survey?.score || 0}%</Score>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ width: '22%' }}>
                        <InfoTitle>Referrability:</InfoTitle>
                      </TableCell>
                      <TableCell>
                        <YesNo className={survey?.isRecommended ? 'yes' : 'no'}>
                          {survey?.isRecommended ? 'Yes' : 'No'}
                        </YesNo>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ width: '22%' }}>
                        <InfoTitle>Survey Details:</InfoTitle>
                      </TableCell>
                      <TableCell>{survey?.note || '-'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Question</TableCell>
                      <TableCell>Score</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {survey?.questions.map((question) => (
                      <TableRow key={`question-${question.sequenceNumber}`}>
                        <TableCell>Question {question.sequenceNumber + 1}</TableCell>
                        <TableCell>{question.text}</TableCell>
                        <TableCell>
                          <Score className={scoreClass(question?.score || 0)}>{question?.score || 0}%</Score>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </DialogContent>
      </DialogContentWrapper>
    </Dialog>
  );
};
